import { useRouter } from 'next/router';
import NextNProgress from 'nextjs-progressbar';

/**
 * The navigation progress bar, and the one rule it has: **it must never unmount.**
 *
 * `nextjs-progressbar` registers `routeChangeStart` / `routeChangeComplete` / `routeChangeError`
 * in a `useEffect` with an empty dependency array, and its cleanup calls `Router.events.off` on
 * all three. nprogress itself is a module-level singleton living on `document.body`, so whoever
 * called `start()` is not necessarily the one who gets to call `done()`, and nothing reconciles
 * the two. Unmount this component between a start and its completion and the bar is stranded:
 * it trickles to ~90% and stays there, with the corner spinner turning, until the next
 * navigation happens to restart it.
 *
 * It used to be mounted *inside* `AuthGuard`, which is the one component in the tree that swaps
 * its children for a loader (`auth-guard.tsx:67`) exactly while a login is handing over to its
 * destination. `employer-app` has always mounted its bar outside the guard; this is now the
 * same shape.
 *
 * ## Suppression is visual, and deliberately not an unmount
 *
 * `nprogress.css` is never imported anywhere in this repo, so the only styling the bar has is
 * the `<style>` tag this component renders — which is why returning `null` from the old
 * `GuardLoaderComponent` wrapper hid it. Rendering no CSS hides it in exactly the same way
 * while leaving the listeners attached, so the suppressed routes keep the behaviour they were
 * given and stop being able to strand the bar for every route after them.
 *
 * The three routes below drive `router.push` on every filter change, which is why somebody
 * silenced the bar there in the first place. That intent is preserved verbatim.
 */
const isSuppressed = (pathname: string): boolean =>
  pathname.includes('/cerco-lavoro') || pathname === '/candidati' || pathname === '/candidature';

export const ProgressBar = () => {
  const { pathname } = useRouter();

  return (
    <NextNProgress color={'#8e69ce'} transformCSS={(css) => (isSuppressed(pathname) ? <></> : <style>{css}</style>)} />
  );
};

export default ProgressBar;
